









































































import { Component, Vue } from 'vue-property-decorator';
import { Getter, Action, Mutation } from 'vuex-class';

import { PublishedApp, InstallAppViewModel } from '../models';
import { appsApi } from '../services/apps';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import { AppApiFactory } from '../services/appApiFactory';
import Toast from '@/shared/services/toast';
import { GET_CHANNEL_IDENTITIES } from '@/store/channel-identities/actionTypes';
import AppUninstallNotAllowedModal from './AppUninstallNotAllowedModal.vue';
import appInsights from '@/shared/services/appInsights';
import { ADD_INSTALLED_APP, REMOVE_INSTALLED_APP } from '@/store/app/mutationTypes';

@Component({
	components: { AppUninstallNotAllowedModal }
})
export default class AppModal extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Getter public currentApp!: PublishedApp;
	@Getter public isWorkspaceFeatureEnabled!: (key: string) => boolean;
	@Mutation(ADD_INSTALLED_APP) public addInstalledApp: (appId: string) => void;
	@Mutation(REMOVE_INSTALLED_APP) public removeInstalledApp: (appId: string) => void;
	@Action(GET_CHANNEL_IDENTITIES) public loadChannelIdentities: any;

	public tabs: string[] = [];
	public activeTab: string;
	public actionInProgress: boolean = false;

	public showAppUninstallNotAllowedModal: boolean = false;

	public created(): void {
		if (this.currentApp == null) {
			this.showAppsStore();
			return;
		}

		if (this.currentApp.isInstalled && this.currentApp.requiresInstallConfig) {
			this.showAppConfig();
			return;
		}

		this.showAppOverview();
	}

	public isActiveTab(tab: string): boolean {
		return this.activeTab === tab;
	}

	public canInstall(): boolean {
		if (this.isAppLive()) {
			return true;
		}

		if (this.isWorkspaceFeatureEnabled(`app_${this.currentApp.id}`)) {
			return true;
		}

		return false;
	}

	public hasStatusBadge(): boolean {
		return !this.isAppLive();
	}

	public getStatusBadgeText(): string {
		if (!this.isAppLive()) {
			return 'Coming Soon!';
		}

		return '';
	}

	public installApp(): void {
		appInsights.trackEvent({ name: 'App install clicked' }, this.eventProperties);
		if (!this.canInstall()) {
			return;
		}

		if (this.actionInProgress) {
			return;
		}

		this.actionInProgress = true;

		const req = {
			appId: this.currentApp.id,
			published: true
		} as InstallAppViewModel;

		appsApi.install(this.currentWorkspace.id, req).then(result => {
			appInsights.trackEvent({ name: 'App installed' }, this.eventProperties);

			// update apps list
			this.currentApp.isInstalled = true;
			this.currentApp.installedAppId = result.installedAppId;
			if (this.currentApp.requiresInstallConfig) {
				this.showAppConfig();
			}
			this.addInstalledApp(this.currentApp.id);
		}).finally(() => {
			this.actionInProgress = false;
		});
	}

	public uninstallApp(): void {
		appInsights.trackEvent({ name: 'App uninstall clicked' }, this.eventProperties);
		if (this.actionInProgress) {
			return;
		}

		this.actionInProgress = true;

		const specificAppApi = AppApiFactory.get(this.currentApp.id);

		if (specificAppApi && !specificAppApi.canUninstall()) {
			this.showAppUninstallNotAllowedModal = true;
			this.actionInProgress = false;
			return;
		}

		let uninstallPromise: Promise<void>;

		if (specificAppApi) {
			uninstallPromise = specificAppApi.uninstall(this.currentWorkspace.id, this.currentApp.installedAppId)
				.then(() => {
					return appsApi.uninstall(this.currentWorkspace.id, this.currentApp.installedAppId);
				});
		} else {
			uninstallPromise = appsApi.uninstall(this.currentWorkspace.id, this.currentApp.installedAppId);
		}

		uninstallPromise // either one or both of the above two promises
			.then(() => {
				appInsights.trackEvent({ name: 'App uninstalled' }, this.eventProperties);

				// update apps list
				this.currentApp.isInstalled = false;
				this.currentApp.installedAppId = '';
				this.showAppOverview();
				this.loadChannelIdentities(true);
				this.removeInstalledApp(this.currentApp.id);
			})
			.catch((err) => {
				Toast.showError(err.message);
			})
			.finally(() => {
				this.actionInProgress = false;
			});
	}

	private showAppsStore(): void {
		this.$router.push({ name: 'apps' });
	}

	private showAppOverview(): void {
		this.activeTab = 'Overview';
		this.$router.push({ name: 'app-overview'});
	}

	private showAppConfig(): void {
		this.activeTab = 'Config';
		this.$router.push({ name: 'app-config'});
	}

	private isAppLive(): boolean {
		return !this.currentApp.status || this.currentApp.status === 'live';
	}

	public get uninstallNotAllowedMessage(): string {
		const specificAppApi = AppApiFactory.get(this.currentApp.id);
		return specificAppApi.getUninstallNotAllowedMessage();
	}

	private get eventProperties(): { [key: string]: any } {
		return {
			appId: this.currentApp.id,
			workspaceId: this.currentWorkspace.id
		};
	}
}
